import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useScrollLock } from '../../_utils/useScrollLock';
import useProductShopData from './useProductShopData';
import useCart from './useCart';
import ProductShopGrid from './ProductShopGrid';
import ProductDetail from './ProductDetail';
import ProductCart from './ProductCart';
import ProductCheckout from './ProductCheckout';
import ProductConfirmation from './ProductConfirmation';
import AuthPromptModal from '../newSalon/SalonProfilePage/AuthPromptModal';
import './ProductShop.css';

// Buying requires sign-in (same as service bookings / gift cards).
const hasToken = () => !!localStorage.getItem('accessToken');

// ProductShopModal — the customer product storefront as a modal: a slide-up
// sheet on mobile, a centred panel on desktop (CSS handles the breakpoint, like
// the gift-card shop). Unlike the single-item voucher wizard, this is a
// multi-item basket: the modal switches between VIEWS (shop -> detail -> cart
// -> checkout -> confirmation) and holds the cart.

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
);
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M18 6L6 18M6 6l12 12" /></svg>
);
const BasketIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 11h14l-1 9a2 2 0 0 1-2 1.8H8a2 2 0 0 1-2-1.8L5 11z" />
    <path d="M9 11V8a3 3 0 0 1 6 0v3" />
  </svg>
);

const TITLES ={ shop: 'Shop', detail: 'Shop', cart: 'Basket', checkout: 'Checkout', confirmation: 'Order confirmed' };

const ProductShopModal = ({ salonIdOrSlug, salonSlug, initialProductId, initialView, onClose }) => {
  const { loading, salonId, salonName, currency, salonLogo, products, categories } = useProductShopData(salonIdOrSlug);
  const cart = useCart();
  const { lines, subtotal } = useMemo(() => cart.build(products), [cart, products]);
  // Read once at mount; the modal remounts on return from the login flow.
  const loggedIn = useMemo(hasToken, []);

  // Deep-link: open straight to a product's detail when launched from a
  // salon-page preview card, or to checkout when restored after a sign-in.
  const [view, setView] = useState(initialView || (initialProductId ? 'detail' : 'shop')); // shop | detail | cart | checkout | confirmation
  const [activeProductId, setActiveProductId] = useState(initialProductId || null);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [dir, setDir] = useState(1);                    // 1 forward, -1 back (drives slide)
  const [closing, setClosing] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [history, setHistory] = useState([]);           // breadcrumb of {view, productId} for the back button
  const basketBtnRef = useRef(null);
  const bodyRef = useRef(null);

  // Shared ref-counted lock: overlapping modals must not restore each
  // other's locked styles (see SalonProfilePage/ConfirmBookingModal).
  useScrollLock(true);

  const handleClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => onClose && onClose(), 300);
  }, [closing, onClose]);

  const goto = useCallback((next, d = 1) => {
    setDir(d);
    setView(next);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, []);

  // Forward navigation that remembers where we came from, so the header back
  // button returns to the previous page (e.g. detail → basket → back → detail)
  // instead of always the shop grid.
  const push = useCallback((next, productId) => {
    setHistory((h) => [...h, { view, productId: activeProductId }]);
    if (productId !== undefined) setActiveProductId(productId);
    goto(next, 1);
  }, [view, activeProductId, goto]);

  const goBack = useCallback(() => {
    if (history.length === 0) { handleClose(); return; }
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setActiveProductId(prev.productId);
    goto(prev.view, -1);
  }, [history, handleClose, goto]);

  // Reset to the grid (Continue shopping / after an order) and forget history.
  const goShop = useCallback(() => { setHistory([]); goto('shop', -1); }, [goto]);

  const activeProduct = useMemo(() => products.find((p) => p.id === activeProductId) || null, [products, activeProductId]);

  // Keep the address bar in sync with the open product, so the URL is always
  // shareable and matches the admin Copy-link format (/shop?product=ID).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (view === 'detail' && activeProductId) {
      if (params.get('product') !== String(activeProductId)) {
        params.set('product', activeProductId);
        window.history.replaceState({}, document.title, `${window.location.pathname}?${params.toString()}`);
      }
    } else if (params.has('product')) {
      params.delete('product');
      const q = params.toString();
      window.history.replaceState({}, document.title, q ? `${window.location.pathname}?${q}` : window.location.pathname);
    }
  }, [view, activeProductId]);

  // A deep-linked product that isn't in the catalogue (taken offline, bad id)
  // falls back to the shop grid instead of an empty pane.
  useEffect(() => {
    if (!loading && view === 'detail' && activeProductId && !activeProduct) {
      goShop();
    }
  }, [loading, view, activeProductId, activeProduct, goShop]);

  const openProduct = useCallback((id) => { push('detail', id); }, [push]);

  // Adding is acknowledged by the header basket itself: it bobs to "catch" the
  // new count, which drops in from above (psBasketCatch / psBadgeDrop in
  // ProductShop.css). Cleaner than flinging the product photo across the screen.
  const handleAdd = useCallback((id, qty = 1, variantId = null) => {
    // Only a real variant id goes in the basket. The card used to hand its
    // image element here (a leftover from the fly-to-basket animation), which
    // was stored as the variant id: the line never resolved, the basket showed
    // "empty" with a count of 1, and persisting it threw on the circular DOM
    // reference — so no product added from a card could be bought online.
    const vid = typeof variantId === 'string' && variantId ? variantId : null;
    cart.addItem(id, qty, vid);
  }, [cart]);

  // Buying requires sign-in (mirrors the gift-card shop / booking). Persist a
  // return flag — the basket already persists via useCart — then open the auth
  // prompt; the salon page re-opens the shop straight to checkout on return.
  const persistDraftAndPromptAuth = useCallback(() => {
    try {
      localStorage.setItem('pendingProductCheckout', JSON.stringify({ returnUrl: window.location.pathname }));
    } catch (e) { /* storage unavailable — prompt anyway */ }
    setShowAuthPrompt(true);
  }, []);

  // Gate at checkout entry: sign in before the delivery/details form, so the
  // order is tied to the buyer's account.
  const handleCheckout = useCallback(() => {
    if (!loggedIn) { persistDraftAndPromptAuth(); return; }
    push('checkout');
  }, [loggedIn, persistDraftAndPromptAuth, push]);

  return (
    <>
    <div className={`ps-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`ps-modal ${closing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Shop">
        <header className="ps-header">
          <button type="button" className="ps-iconbtn" onClick={goBack} aria-label="Back"><BackIcon /></button>
          <h2 className="ps-title">{TITLES[view] || 'Shop'}</h2>
          <div className="ps-header-right">
            {view !== 'confirmation' && (
              <button type="button" ref={basketBtnRef} className="ps-basket" onClick={() => push('cart')} aria-label={`Basket, ${cart.count} item${cart.count === 1 ? '' : 's'}`}>
                <span className="ps-basket-icon" key={`icon-${cart.count}`}><BasketIcon /></span>
                {cart.count > 0 && <span className="ps-basket-badge" key={cart.count}>{cart.count}</span>}
              </button>
            )}
            <button type="button" className="ps-iconbtn" onClick={handleClose} aria-label="Close"><CloseIcon /></button>
          </div>
        </header>

        <div className="ps-body" ref={bodyRef}>
          <div className={`ps-view ${dir > 0 ? 'from-right' : 'from-left'}`} key={`${view}-${activeProductId || ''}`}>
            {view === 'shop' && (
              <ProductShopGrid products={products} categories={categories} currency={currency} loading={loading} onOpen={openProduct} onAdd={handleAdd} />
            )}
            {view === 'detail' && activeProduct && (
              <ProductDetail
                product={activeProduct}
                products={products}
                currency={currency}
                cartItems={cart.items}
                onAdd={handleAdd}
                onSetQty={cart.setQty}
                onViewBasket={() => push('cart')}
                onOpen={openProduct}
              />
            )}
            {view === 'cart' && (
              <ProductCart lines={lines} subtotal={subtotal} currency={currency} onSetQty={cart.setQty} onRemove={cart.removeItem} onCheckout={handleCheckout} onContinue={goShop} />
            )}
            {view === 'checkout' && (
              <ProductCheckout
                lines={lines} subtotal={subtotal} currency={currency} salonName={salonName}
                salonIdOrSlug={salonIdOrSlug} salonId={salonId}
                onBack={goBack}
                onRequireAuth={persistDraftAndPromptAuth}
                onPaid={(order) => { setConfirmedOrder(order); cart.clear(); setHistory([]); goto('confirmation', 1); }}
              />
            )}
            {view === 'confirmation' && (
              <ProductConfirmation order={confirmedOrder} currency={currency} salonName={salonName} onClose={handleClose} onContinue={goShop} />
            )}
          </div>
        </div>
      </div>
    </div>

    <AuthPromptModal isOpen={showAuthPrompt} onClose={() => setShowAuthPrompt(false)} context="product" />
    </>
  );
};

export default ProductShopModal;
